import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AllInitializationCookie, AuthCookie, CookieReadWriteService } from '../../services/authentication/cookie-read-write.service';
import { ShellContextFacade } from '../../services/shell-context/shell-context-facade.service';
import { AuthService, SessionInfo } from '../../services/authentication/auth.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Consts } from '@shared/consts';
import { PostLoginService } from '../../services/authentication/post-login.service';
import { UrlTree } from '@angular/router';
import { AuthAngularService } from '@itero/auth-angular';
import { JwtDecodeWrapper } from '@core/app-initialization/jwt-decode.service';

@Injectable({
	providedIn: 'root',
})
export class AppInitializationService {
	private cookieReadWriteService = inject(CookieReadWriteService);
	private shellContextFacade = inject(ShellContextFacade);
	private authService = inject(AuthService);
	private eupRoutesService = inject(EupRoutesService);
	private postLoginService = inject(PostLoginService);
	private authAngularService = inject(AuthAngularService);

	get allInitializationCookie(): AllInitializationCookie {
		return this.cookieReadWriteService.allInitializationCookie;
	}

	init(cookie: AllInitializationCookie): Observable<UrlTree | boolean> {
		this.initWithAuthCookie(cookie.authCookie);

		if (this.isAuthenticated) {
			return of(true);
		}

		const decodedToken = JwtDecodeWrapper.decode(cookie.authCookie.accessToken);

		let email = '';

		if ('email' in decodedToken && typeof decodedToken.email === 'string') {
			email = decodedToken.email;
		} else if ('sub' in decodedToken && typeof decodedToken.sub === 'string') {
			email = decodedToken.sub;
		} else {
			return of(false);
		}

		return this.postLoginService.init(
			this.shellContextFacade.sessionInfo,
			cookie.infraCookie.region,
			cookie.authCookie.identityProvider,
			email
		);
	}

	private initWithAuthCookie(cookie: AuthCookie) {
		// write to Auth-angular
		this.authAngularService.updateAuthInfo({
			accessToken: cookie.accessToken,
			sessionType: Consts.SessionType,
			sessionId: cookie.sessionId,
			authUrl: this.eupRoutesService.iTeroWebAuthApiUrl,
		});

		const sessionInfo: SessionInfo = {
			sessionId: cookie.sessionId,
			accessToken: cookie.accessToken,
			sessionType: Consts.SessionType,
			idToken: cookie.idToken,
		};

		// write to in-memory shell-context
		this.shellContextFacade.updateSessionInfo(sessionInfo);
	}

	private get isAuthenticated() {
		return this.authService.isAuthenticated();
	}
}
